<template>
    <!-- MY1-3079: "Auto-tracked" / "Auto-tracking available" provenance label.
         The whole label is the click target (design: "label is clickable, not
         only Icon"); manual-only cards render nothing. -->
    <UiTooltip
        v-if="showLabel"
        :text="tooltip"
        placement="top"
        :interactive="!isClickable"
        trigger-class="inline-flex max-w-full"
    >
        <template #trigger>
            <component
                :is="isClickable ? 'button' : 'span'"
                :type="isClickable ? 'button' : undefined"
                class="inline-flex items-center leading-5 text-slate-500 dark:text-slate-400 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 dark:focus-visible:ring-slate-500 max-w-full"
                :class="[
                    compact ? 'text-xs gap-1' : 'text-sm gap-2',
                    isClickable
                        ? 'cursor-pointer hover:text-slate-700 dark:hover:text-slate-200'
                        : 'cursor-default',
                ]"
                :data-auto-tracking-state="state"
                :aria-label="isClickable ? `${label}. ${tooltip}` : undefined"
                @click.stop.prevent="handleClick"
            >
                <Icon
                    v-if="isAutoTracked"
                    icon="arrows-repeat"
                    :class="compact ? 'w-3 h-3' : 'w-4 h-4'"
                    class="shrink-0"
                />
                <span class="truncate">{{ label }}</span>
                <Icon
                    v-if="!isAutoTracked"
                    icon="info"
                    :class="compact ? 'w-3 h-3' : 'w-4 h-4'"
                    class="shrink-0"
                />
            </component>
        </template>
    </UiTooltip>
</template>

<script setup>
import { router } from '@inertiajs/vue3';
import Icon from '@/components/ui/icon/icon.vue';
import UiTooltip from '@/components/ui/tooltip/ui-tooltip.vue';
import { useBenefitAutoTracking } from '@/composables/useBenefitAutoTracking';
import { useWalletStore } from '@/store/wallet';
import { useCardBenefitsStore } from '@/store/card-benefits';

const props = defineProps({
    // The benefit payload's `auto_tracking` object (BenefitAutoTrackingPresenter).
    autoTracking: {
        type: Object,
        default: null,
    },
    // Tighter type/gap for table rows.
    compact: {
        type: Boolean,
        default: false,
    },
    // MY1-3083: the viewer owns this card, so a `spending_disabled` label may
    // turn My Spending on in place. Leave off for other members' cards.
    canEnableSpending: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['connect', 'upgrade', 'enable-spending']);

const { state, isAutoTracked, showLabel, label, tooltip, action, isClickable } =
    useBenefitAutoTracking(() => props.autoTracking, {
        canEnableSpending: () => props.canEnableSpending,
    });

function handleClick() {
    if (!isClickable.value) {
        return;
    }

    if (action.value === 'connect') {
        emit('connect');
        // Same entry point as the bonus-tracking connect tip (MY1-3044): the
        // wallet page owns the MX connect modal and opens it when the store
        // flag is set, so go there with the flag raised.
        useWalletStore().requestConnectModal();
        router.visit('/wallet');

        return;
    }

    if (action.value === 'upgrade') {
        emit('upgrade');
        useCardBenefitsStore().setShowUpgradeModal(true);

        return;
    }

    if (action.value === 'enable-spending') {
        emit('enable-spending');
        // In place, like the wallet's prompt (MY1-3077): the user keeps their
        // spot and the benefits refresh into their auto-tracked state.
        useCardBenefitsStore().enableSpending();
    }
}
</script>
